import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import { TicketTableDrawer } from "pages/modules/home/components/TicketTableDrawer";
import { useState } from "react";
import { toOverviewCardType, trackAiHome } from "../../analytics";
import {
  HOME_SCREEN_FILTER,
  type OverviewCountCard,
  useOverviewCounts,
} from "../../hooks/useOverviewCounts";
import { OverviewCard } from "../../main-legacy/components/OverviewCard";
import { dashboardScopeAtom } from "../../store";
import { ChatSuggestions } from "./ChatSuggestions";

interface ChatEmptyStateProps {
  /** Sends the clicked chip's prompt to Helena. */
  onAskSuggestion: (prompt: string) => void;
}

/** The opening summary — reads off the same counts as the cards below it. */
const buildQueueSummary = (cards: OverviewCountCard[]) => {
  const countOf = (id: OverviewCountCard["id"]) =>
    cards.find((card) => card.id === id)?.value ?? 0;

  return `I've gone through today's queue. You've got ${countOf("at_risk_count")} tickets approaching SLA and ${countOf("overdue_count")} already overdue — that's where I'd start. ${countOf("unassigned_count")} unassigned tickets are waiting to be picked up.`;
};

export const ChatEmptyState = ({ onAskSuggestion }: ChatEmptyStateProps) => {
  const theme = useTheme();
  const firstName = useGlobalStore((state) => state.firstName);
  const { cards, isLoading, resourceIds, period, interval } =
    useOverviewCounts();
  const scope = useAtomValue(dashboardScopeAtom);
  const [selectedFilterType, setSelectedFilterType] = useState<string | null>(
    null,
  );

  const handleCardClick = (card: OverviewCountCard) => {
    trackAiHome("home_overview_card_clicked", {
      card_type: toOverviewCardType(card.id),
      value_shown: card.value,
      time_filter: period,
      scope_mode: scope.mode,
      screen_mode: "ai",
    });
    setSelectedFilterType(card.filterType);
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0, gap: "24px", overflowY: "auto" }}>
      <Stack sx={{ gap: "16px" }}>
        <Typography variant="h6" sx={{ color: theme.palette.lightBg.medium }}>
          {firstName ? `Hey, ${firstName}. 👋` : "Hey there. 👋"}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width="70%" height={20} />
        ) : (
          <Typography
            variant="body-medium"
            sx={{ color: theme.palette.lightBg.high }}
          >
            {buildQueueSummary(cards)}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" sx={{ gap: "12px" }}>
        {cards.map((card) =>
          isLoading ? (
            <Skeleton
              key={card.id}
              variant="rounded"
              height={64}
              sx={{ flex: 1, borderRadius: "8px" }}
            />
          ) : (
            <OverviewCard
              key={card.id}
              label={card.label}
              value={card.value}
              tone={card.tone}
              onClick={() => handleCardClick(card)}
            />
          ),
        )}
      </Stack>

      <Stack sx={{ mt: "auto", pb: "16px" }}>
        <ChatSuggestions onSelect={onAskSuggestion} />
      </Stack>

      {selectedFilterType && (
        <TicketTableDrawer
          open
          onClose={() => setSelectedFilterType(null)}
          filter={{
            type: selectedFilterType,
            resourceIds,
            view: period,
            // Keeps the drilldown on the same window the counts came from.
            interval,
            otherfilters: HOME_SCREEN_FILTER,
          }}
        />
      )}
    </Stack>
  );
};
